import React, { useEffect, useMemo, useState } from "react";
import { FilterCheckboxListProps } from "./FilterCheckboxList.types";
import {
  ButtonWrapper,
  Checkbox,
  EmptyOptions,
  FilterContainer,
  FilterCount,
  FilterHeader,
  FilterTitle,
  OptionCount,
  OptionItem,
  OptionLabel,
  OptionWrapper,
  ResetButton,
  SaveButton,
  SearchInput,
  SearchWrapper,
} from "./FilterCheckboxList.styles";

const FilterCheckboxList: React.FC<FilterCheckboxListProps> = ({
  options,
  onSave,
  onReset,
  selectedOptions,
  title = "Фильтр",
  searchable = false,
  searchPlaceholder = "Найти",
  emptyMessage = "Ничего не найдено",
}) => {
  const [selected, setSelected] = useState<Set<string>>(
    new Set(selectedOptions || []),
  );
  const [query, setQuery] = useState("");

  useEffect(() => {
    setSelected(new Set(selectedOptions || []));
  }, [selectedOptions]);

  const visibleOptions = useMemo(() => {
    const normalizedQuery = query.trim().toLocaleLowerCase("ru-RU");
    if (!searchable || !normalizedQuery) {
      return options;
    }

    return options.filter((option) =>
      option.label.toLocaleLowerCase("ru-RU").includes(normalizedQuery),
    );
  }, [options, query, searchable]);

  const handleToggle = (option: string) => {
    setSelected((previous) => {
      const next = new Set(previous);
      if (next.has(option)) {
        next.delete(option);
      } else {
        next.add(option);
      }
      return next;
    });
  };

  const handleReset = () => {
    setSelected(new Set());
    setQuery("");
    onReset();
  };

  return (
    <FilterContainer role="dialog" aria-label={`Фильтр: ${title}`}>
      <FilterHeader>
        <FilterTitle>{title}</FilterTitle>
        <FilterCount>{selected.size ? `выбрано ${selected.size}` : ""}</FilterCount>
      </FilterHeader>

      {searchable && (
        <SearchWrapper>
          <SearchInput
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={searchPlaceholder}
            aria-label={`Поиск: ${title}`}
          />
        </SearchWrapper>
      )}

      <OptionWrapper>
        {visibleOptions.length ? (
          visibleOptions.map((option) => (
            <OptionItem key={option.value}>
              <Checkbox
                type="checkbox"
                checked={selected.has(option.value)}
                onChange={() => handleToggle(option.value)}
              />
              <OptionLabel title={option.label}>{option.label}</OptionLabel>
              {typeof option.count === "number" && (
                <OptionCount>{option.count}</OptionCount>
              )}
            </OptionItem>
          ))
        ) : (
          <EmptyOptions>{emptyMessage}</EmptyOptions>
        )}
      </OptionWrapper>

      <ButtonWrapper>
        <SaveButton type="button" onClick={() => onSave(Array.from(selected))}>
          Применить
        </SaveButton>
        <ResetButton
          type="button"
          onClick={handleReset}
          disabled={selected.size === 0 && (selectedOptions?.length ?? 0) === 0}
        >
          Сбросить
        </ResetButton>
      </ButtonWrapper>
    </FilterContainer>
  );
};

export default FilterCheckboxList;
